import React, { useState, useRef, useEffect, useContext, useCallback } from "react";
import { useTheme } from "@material-ui/core/styles";

import { useHistory } from "react-router-dom";
import { format, parseISO, isSameDay } from "date-fns";
// import { SocketContext } from "../../context/Socket/SocketContext";

import useSound from "use-sound";

import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Avatar from "@material-ui/core/Avatar";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Badge from "@material-ui/core/Badge";
import ChatIcon from "@material-ui/icons/Chat";
import CloseIcon from "@material-ui/icons/Close";
import { Tooltip } from "@material-ui/core";

import useTickets from "../../hooks/useTickets";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import Favicon from "react-favicon";
import { getBackendUrl } from "../../config";
import defaultLogoFavicon from "../../assets/favicon.ico";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	notificationItem: {
		cursor: "pointer",
		position: "relative",
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
	dismissButton: {
		padding: 4,
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.error.main,
			backgroundColor: "transparent",
		},
	},
	contactName: {
		fontWeight: 600,
		fontSize: "0.875rem",
	},
	lastMessage: {
		fontSize: "0.8rem",
		color: theme.palette.text.secondary,
		maxWidth: "200px",
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	messageTime: {
		fontSize: "0.7rem",
		color: theme.palette.text.secondary,
	},
	queueColor: {
		width: 4,
		height: "100%",
		position: "absolute",
		top: 0,
		left: 0,
	},
}));

const NotificationsPopOver = (volume) => {
	const classes = useStyles();
	const theme = useTheme();

	const history = useHistory();
	// const socketManager = useContext(SocketContext);
	const { user, socket } = useContext(AuthContext);
	const { profile, queues } = user;

	const ticketIdUrl = +history.location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const queueIds = queues.map((q) => q.id);
	const { get: getSetting } = useCompanySettings();
    const { setCurrentTicket, setTabOpen } = useContext(TicketsContext);

	const [showTicketWithoutQueue, setShowTicketWithoutQueue] = useState(false);
	const [showNotificationPending, setShowNotificationPending] = useState(false);
	const [showGroupNotification, setShowGroupNotification] = useState(false);

	// Refs para garantir que os valores mais recentes são usados dentro dos listeners de socket
	const showNotificationPendingRef = useRef(false);
	const showGroupNotificationRef = useRef(false);

	const [, setDesktopNotifications] = useState([]);

	// Cache de mensagens já processadas para evitar notificações duplicadas/triplicadas
	const processedMessageIdsRef = useRef(new Set());
	const lastSoundTimestampRef = useRef(0);

	const { tickets } = useTickets({
		withUnreadMessages: "true"
	});

	const [play] = useSound(alertSound, {
		volume: typeof volume?.volume === "number" ? volume.volume : (typeof volume === "number" ? volume : 0.5),
	});
	const soundAlertRef = useRef();

	const historyRef = useRef(history);

	useEffect(() => {
		const fetchSettings = async () => {
			try {
				const setting = await getSetting(
					{
						"column": "showNotificationPending"
					}
				);
				if (
					setting.showNotificationPending === true ||
					setting.showNotificationPending === "true" ||
					setting.showNotificationPending === "enabled"
				) {
					setShowNotificationPending(true);
					showNotificationPendingRef.current = true;
				}

				if (user.allTicket === "enable" || user.allTicket === "enabled") {
					setShowTicketWithoutQueue(true);
				}
				if (user.allowGroup === true || user.allowGroup === "true") {
					setShowGroupNotification(true);
					showGroupNotificationRef.current = true;
				}
			} catch (err) {
				toastError(err);
			}
		}

		fetchSettings();
	}, [setShowTicketWithoutQueue, setShowNotificationPending]);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			// Browser doesn't support notifications
		}
	}, [play]);

	useEffect(() => {
		// Merge: adicionar tickets do HTTP que ainda não estão nas notificações
		// sem sobrescrever notificações já adicionadas pelo socket em tempo real
		setNotifications(prevState => {
			const existingIds = new Set(prevState.map(t => t.id));
			const newFromHttp = tickets.filter(t => !existingIds.has(t.id));
			if (newFromHttp.length === 0 && prevState.length > 0) {
				// Atualizar dados dos tickets existentes com dados mais completos do HTTP
				return prevState.map(existing => {
					const updated = tickets.find(t => t.id === existing.id);
					return updated || existing;
				});
			}
			// Na primeira carga (prevState vazio), usar tickets do HTTP diretamente
			if (prevState.length === 0) {
				return tickets;
			}
			return [...prevState, ...newFromHttp];
		});
	}, [tickets]);

	// Sincronizar tickets do hook com notificações
	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const companyId = user.companyId;
		
		if (user.id && socket && typeof socket.on === 'function' && typeof socket.off === 'function') {
			const queueIds = queues.map((q) => q.id);

			const onConnectNotificationsPopover = () => {
				socket.emit("joinNotification");
			}

			const onCompanyTicketNotificationsPopover = (data) => {
				if (data.action === "updateUnread" || data.action === "delete") {
					setNotifications(prevState => {
						const ticketIndex = prevState.findIndex(t => t.id === data.ticketId);
						if (ticketIndex !== -1) {
							prevState.splice(ticketIndex, 1);
							return [...prevState];
						}
						return prevState;
					});

					setDesktopNotifications(prevState => {
						const notfiticationIndex = prevState.findIndex(
							n => n.tag === `ticket-${data.ticketId}` || n.tag === String(data.ticketId)
						);
						if (notfiticationIndex !== -1) {
							prevState[notfiticationIndex].close();
							prevState.splice(notfiticationIndex, 1);
							return [...prevState];
						}
						return prevState;
					});
				}

				if (data.action === "update" && data.ticket) {
					setNotifications(prevState => {
						const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
						if (ticketIndex !== -1) {
							prevState[ticketIndex] = { ...prevState[ticketIndex], ...data.ticket };
							return [...prevState];
						}
						return prevState;
					});

					if (data.ticket.isMuted) {
						setDesktopNotifications(prevState => {
							const notfiticationIndex = prevState.findIndex(
								n => n.tag === `ticket-${data.ticket.id}` || n.tag === String(data.ticket.id)
							);
							if (notfiticationIndex !== -1) {
								prevState[notfiticationIndex].close();
								prevState.splice(notfiticationIndex, 1);
								return [...prevState];
							}
							return prevState;
						});
					}
				}
			};

// Adicione esta lógica no componente NotificationsPopOver
const onCompanyAppMessageNotificationsPopover = (data) => {
    const isCreate = data.action === "create";
    const notFromMe = !data.message?.fromMe;
    const notRead = !data.message?.read;

    if (!isCreate || !notFromMe || !notRead) {
        return;
    }

    // Deduplicação intra-aba: descartar imediatamente evento duplicado da mesma mensagem
    const messageId = data.message?.id || data.message?.wid;
    if (messageId) {
        if (processedMessageIdsRef.current.has(messageId)) {
            return;
        }
        processedMessageIdsRef.current.add(messageId);
        setTimeout(() => {
            if (processedMessageIdsRef.current) {
                processedMessageIdsRef.current.delete(messageId);
            }
        }, 30000);
    }
    
    // Unificar dados do ticket com prioridade e fallback seguro
    const ticketData = {
        ...(data.message?.ticket || {}),
        ...(data.ticket || {})
    };

    const rawIsMuted = (data.ticket && data.ticket.isMuted !== undefined)
        ? data.ticket.isMuted
        : data.message?.ticket?.isMuted;

    const rawMutedUntil = (data.ticket && data.ticket.mutedUntil !== undefined)
        ? data.ticket.mutedUntil
        : data.message?.ticket?.mutedUntil;

    ticketData.isMuted = rawIsMuted;
    ticketData.mutedUntil = rawMutedUntil;
    
    // Aceitar se: ticket é do usuário OU ticket não tem usuário (disponível para todos)
    const userMatch = !ticketData?.userId || ticketData?.userId === user?.id;
    
    // Aceitar se: ticket está em fila do usuário OU ticket não tem fila (se permitido) OU usuário vê todos os tickets
    const queueMatch = 
        !ticketData?.queueId || // Sem fila
        user?.queues?.some(queue => queue.id === ticketData.queueId) || // Fila do usuário
        user?.allTicket === "enable"; // Usuário vê todos
    
    // Aceitar status: open, pending (se habilitado), group (se habilitado)
    // Bloquear apenas: lgpd, nps, closed
    const ticketStatus = ticketData?.status;
    const isGroup = Boolean(ticketData?.isGroup || ticketStatus === "group");
    const statusMatch = 
        !["lgpd", "nps", "closed"].includes(ticketStatus) && (
            ticketStatus !== "pending" || showNotificationPendingRef.current === true
        ) && (
            !isGroup || showGroupNotificationRef.current === true
        );
    
    const passedConditions = isCreate && notFromMe && notRead && userMatch && queueMatch && statusMatch;
    
    if (passedConditions) {
        
        // Aplicar lógica de permissão para mensagens pending
        const shouldBlurMessages = ticketStatus === "pending" && user.allowSeeMessagesInPendingTickets === "disabled";
        
        // Se deve ocultar a mensagem, modifique o ticket antes de adicioná-lo às notificações
        const ticketToAdd = shouldBlurMessages 
            ? {
                ...ticketData,
                lastMessage: i18n.t("notifications.messageHidden") // ou "Mensagem oculta"
              }
            : ticketData;

        setNotifications(prevState => {
            const ticketIndex = prevState.findIndex(t => t.id === ticketToAdd.id);
            if (ticketIndex !== -1) {
                prevState[ticketIndex] = ticketToAdd;
                return [...prevState];
            }
            return [ticketToAdd, ...prevState];
        });

        const shouldNotNotificate =
            (ticketData.userId && ticketData.userId !== user?.id);

        if (shouldNotNotificate === true) {
            return;
        }

        // Verificar se o ticket está silenciado (isMuted ou mutedUntil futuro)
        const isTicketMuted = 
            Boolean(rawIsMuted) || 
            rawIsMuted === "true" || 
            rawIsMuted === 1 || 
            rawIsMuted === "1" || 
            Boolean(rawMutedUntil && new Date(rawMutedUntil) > new Date());

        if (isTicketMuted) {
            return;
        }

        // Para notificações desktop, também aplicar a lógica de ocultação
        const messageBody = shouldBlurMessages 
            ? i18n.t("notifications.messageHidden")
            : data.message.body;

        handleNotifications({
            ...data,
            ticket: ticketData,
            message: {
                ...data.message,
                body: messageBody
            }
        });
    }
}

			try {
				socket.on("connect", onConnectNotificationsPopover);
				socket.on(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
				socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);

				return () => {
					try {
						socket.off("connect", onConnectNotificationsPopover);
						socket.off(`company-${companyId}-ticket`, onCompanyTicketNotificationsPopover);
						socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageNotificationsPopover);
					} catch (error) {
						console.error('Erro ao remover listeners do NotificationsPopOver:', error);
					}
				};
			} catch (error) {
				console.error('Erro ao adicionar listeners do NotificationsPopOver:', error);
			}
		}
	}, [user, profile, queues, showTicketWithoutQueue, socket, showNotificationPending, showGroupNotification]);

	const handleNotifications = data => {
		const { message, contact, ticket } = data;
		const currentTicket = ticket || message?.ticket;

		const rawIsMuted = (ticket && ticket.isMuted !== undefined) 
			? ticket.isMuted 
			: message?.ticket?.isMuted;

		const rawMutedUntil = (ticket && ticket.mutedUntil !== undefined) 
			? ticket.mutedUntil 
			: message?.ticket?.mutedUntil;

		const isMuted = 
			Boolean(rawIsMuted) || 
			rawIsMuted === "true" || 
			rawIsMuted === 1 || 
			rawIsMuted === "1" || 
			Boolean(rawMutedUntil && new Date(rawMutedUntil) > new Date());

		if (isMuted) {
			return;
		}

		// Deduplicação entre múltiplas abas: se outra aba já disparou o alerta desta mensagem, não repetir
		const messageId = message?.id || message?.wid;
		if (messageId) {
			try {
				const tabAlertKey = `liot_alert_${messageId}`;
				const lastAlert = localStorage.getItem(tabAlertKey);
				if (lastAlert && (Date.now() - Number(lastAlert) < 10000)) {
					return;
				}
				localStorage.setItem(tabAlertKey, String(Date.now()));
				setTimeout(() => {
					try {
						localStorage.removeItem(tabAlertKey);
					} catch (e) {}
				}, 15000);
			} catch (err) {
				// Ignorar erros de quota de localStorage
			}
		}

		const showNotification = () => {
			// Solicitar permissão se ainda não foi decidida
			if (Notification.permission === 'default') {
				Notification.requestPermission().then(permission => {
					if (permission === 'granted') {
						createDesktopNotification(message, contact, currentTicket);
					}
				});
				return;
			}

			if (Notification.permission === 'granted') {
				createDesktopNotification(message, contact, currentTicket);
			}
		};

		showNotification();

		// Controle de áudio (throttle): no mínimo 1200ms entre toques sonoros
		const now = Date.now();
		let canPlaySound = true;
		try {
			const lastSoundKey = "liot_last_sound_time";
			const lastSoundGlobal = Number(localStorage.getItem(lastSoundKey) || 0);
			if (now - lastSoundGlobal < 1200 || now - lastSoundTimestampRef.current < 1200) {
				canPlaySound = false;
			} else {
				localStorage.setItem(lastSoundKey, String(now));
			}
		} catch (e) {
			if (now - lastSoundTimestampRef.current < 1200) {
				canPlaySound = false;
			}
		}

		if (canPlaySound && soundAlertRef.current) {
			lastSoundTimestampRef.current = now;
			soundAlertRef.current();
		}
	};

	const createDesktopNotification = (message, contact, ticket) => {
		try {
			const contactName = contact?.name || ticket?.contact?.name || "Novo contato";
			const contactIcon = contact?.urlPicture || ticket?.contact?.urlPicture || undefined;

			const options = {
				body: `${message.body || ""} - ${format(new Date(), "HH:mm")}`,
				icon: contactIcon,
				tag: `ticket-${ticket.id}`,
				renotify: true,
				silent: false,
			};

			const notification = new Notification(
				`${i18n.t("tickets.notification.message")} ${contactName}`,
				options
			);

			notification.onclick = e => {
				e.preventDefault();
				window.focus();
				setTabOpen(ticket.status);
				historyRef.current.push(`/tickets/${ticket.uuid}`);
			};

			setDesktopNotifications(prevState => {
				const idx = prevState.findIndex(n => n.tag === `ticket-${ticket.id}` || n.tag === String(ticket.id));
				if (idx !== -1) {
					prevState[idx] = notification;
					return [...prevState];
				}
				return [notification, ...prevState];
			});
		} catch (err) {
			console.error("Erro ao criar notificação desktop:", err);
		}
	};

	const handleClick = () => {
		// Solicitar permissão de notificação no primeiro clique (gesto do usuário)
		if ("Notification" in window && Notification.permission === "default") {
			Notification.requestPermission();
		}
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	// Função para solicitar permissão de notificação em resposta à ação do usuário
	const requestNotificationPermission = async () => {
		if (!("Notification" in window)) {
			alert("Este navegador não suporta notificações desktop");
			return;
		}

		if (Notification.permission === 'denied') {
			alert("As notificações foram bloqueadas. Para ativar, acesse as configurações do navegador.");
			return;
		}

		if (Notification.permission !== 'granted') {
			try {
				await Notification.requestPermission();
			} catch (error) {
				console.error('Erro ao solicitar permissão de notificação:', error);
			}
		}
	};

	const NotificationTicket = ({ children }) => {
		return <div onClick={handleClickAway}>{children}</div>;
	};

	// Dispensar notificação sem fechar o ticket
	const handleDismissNotification = useCallback((e, ticketId) => {
		e.stopPropagation();
		setNotifications(prevState => prevState.filter(t => t.id !== ticketId));
		setDesktopNotifications(prevState => {
			const idx = prevState.findIndex(n => n.tag === String(ticketId));
			if (idx !== -1) {
				prevState[idx].close();
				prevState.splice(idx, 1);
				return [...prevState];
			}
			return prevState;
		});
	}, []);

	// Clicar na notificação para abrir o ticket
	const handleNotificationClick = useCallback((ticket) => {
		handleClickAway();
		setTabOpen(ticket.status);
		historyRef.current.push(`/tickets/${ticket.uuid}`);
	}, [setTabOpen]);

	const browserNotification = () => {
		const numbers = "⓿➊➋➌➍➎➏➐➑➒➓⓫⓬⓭⓮⓯⓰⓱⓲⓳⓴";
		if (notifications.length > 0) {
			if (notifications.length < 21) {
				document.title = numbers.substring(notifications.length, notifications.length + 1) + " - " + (theme.appName || "...");
			} else {
				document.title = "(" + notifications.length + ")" + (theme.appName || "...");
			}
		} else {
			document.title = theme.appName || "...";
		}
		return (
			<>
				<Favicon
					animated={true}
					url={(theme?.appLogoFavicon) ? theme.appLogoFavicon : defaultLogoFavicon}
					alertCount={notifications.length}
					iconSize={195}
				/>
			</>
		);
	};

	return (
		<>
			{browserNotification()}

			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				color="inherit"
				style={{ color: "white" }}
			>
				<Badge overlap="rectangular" badgeContent={notifications.length} color="secondary">
					<ChatIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{notifications.length === 0 ? (
						<ListItem>
							<ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map(ticket => (
							<ListItem
								key={ticket.id}
								button
								dense
								className={classes.notificationItem}
								onClick={() => handleNotificationClick(ticket)}
							>
								<span
									className={classes.queueColor}
									style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
								/>
								<ListItemAvatar>
									<Avatar
										src={ticket?.contact?.urlPicture}
										alt={ticket?.contact?.name}
									/>
								</ListItemAvatar>
								<ListItemText
									disableTypography
									primary={
										<Typography noWrap component="span" variant="body2" className={classes.contactName}>
											{ticket?.contact?.name || "Contato"}
										</Typography>
									}
									secondary={
										<>
											<Typography noWrap component="span" variant="body2" className={classes.lastMessage}>
												{ticket.lastMessage || ""}
											</Typography>
											<Typography component="span" variant="body2" className={classes.messageTime} style={{ display: "block" }}>
												{ticket.updatedAt && (() => {
													try {
														const date = parseISO(ticket.updatedAt);
														if (isNaN(date.getTime())) return "";
														return isSameDay(date, new Date())
															? format(date, "HH:mm")
															: format(date, "dd/MM/yyyy");
													} catch { return ""; }
												})()}
											</Typography>
										</>
									}
								/>
								<ListItemSecondaryAction>
									<Tooltip title="Dispensar notificação">
										<IconButton
											size="small"
											className={classes.dismissButton}
											onClick={(e) => handleDismissNotification(e, ticket.id)}
										>
											<CloseIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</ListItemSecondaryAction>
							</ListItem>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;